import React from 'react';

export type SpinnerVariant = 'default' | 'dots' | 'pulse' | 'bars' | 'ring';
export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface LoadingSpinnerProps {
  variant?: SpinnerVariant;
  size?: SpinnerSize;
  customSize?: string;
  color?: string;
  className?: string;
  text?: string;
  fullScreen?: boolean;
  inline?: boolean;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  variant = 'default',
  size = 'md',
  customSize,
  color = 'text-primary',
  className = '',
  text,
  fullScreen = false,
  inline = false
}) => {
  const sizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  const textSizeClasses = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl'
  };

  const renderSpinner = () => {
    const resolvedSize = customSize || sizeClasses[size];
    const borderWidth = size === 'xs' && !customSize ? 'border' : 'border-2';
    const baseClass = `${resolvedSize} ${color} ${className}`;

    switch (variant) {
      case 'dots':
        return (
          <div className={`flex space-x-1 ${className}`}>
            <div className={`${resolvedSize} ${color.replace('text-', 'bg-')} rounded-full animate-bounce`} style={{ animationDelay: '0ms' }}></div>
            <div className={`${resolvedSize} ${color.replace('text-', 'bg-')} rounded-full animate-bounce`} style={{ animationDelay: '150ms' }}></div>
            <div className={`${resolvedSize} ${color.replace('text-', 'bg-')} rounded-full animate-bounce`} style={{ animationDelay: '300ms' }}></div>
          </div>
        );

      case 'pulse':
        return (
          <div className={`${baseClass} rounded-full ${color.replace('text-', 'bg-')} animate-ping`}></div>
        );

      case 'bars':
        return (
          <div className={`flex space-x-1 ${className}`}>
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className={`w-1 ${resolvedSize.split(' ')[1]} ${color.replace('text-', 'bg-')} animate-pulse`}
                style={{ animationDelay: `${i * 100}ms` }}
              ></div>
            ))}
          </div>
        );

      case 'ring':
        return (
          <div className={`${baseClass} animate-spin`}>
            <div className={`${resolvedSize} rounded-full ${borderWidth} border-transparent border-t-current border-r-current`}></div>
          </div>
        );

      default:
        return (
          <div className={`${baseClass} animate-spin rounded-full ${borderWidth} border-transparent border-t-current`}></div>
        );
    }
  };

  if (inline) {
    return renderSpinner();
  }

  const content = (
    <div className="flex flex-col items-center justify-center space-y-3">
      {renderSpinner()}
      {text && (
        <p className={`${textSizeClasses[size]} ${color} animate-pulse font-medium`}>
          {text}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-bg flex items-center justify-center z-50">
        {content}
      </div>
    );
  }

  return content;
};

// Convenience components for common use cases
export const PageLoader: React.FC<{ text?: string }> = ({ text = "" }) => (
  <div className="min-h-screen bg-bg flex items-center justify-center">
    <LoadingSpinner variant="ring" size="lg" text={text} />
  </div>
);

export const InlineLoader: React.FC<{ size?: SpinnerSize; text?: string }> = ({ 
  size = 'md', 
  text 
}) => (
  <div className="flex items-center justify-center p-4">
    <LoadingSpinner variant="dots" size={size} text={text} />
  </div>
);

export const ButtonLoader: React.FC<{ size?: SpinnerSize; color?: string; className?: string }> = ({
  size = 'sm',
  color = 'text-white',
  className = ''
}) => (
  <LoadingSpinner variant="default" size={size} color={color} inline className={className} />
);

export default LoadingSpinner;